import { LoginOutlined } from "@ant-design/icons";
import { useOidc } from "@axa-fr/react-oidc";
import { Button, Result, Space, Spin, Typography } from "antd";
import { CSSProperties, FC, ReactNode, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { centerContainerStyle } from "./styles";

const antdResultStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  transform: "translateY(-25%)",
  background: "rgba(127, 127, 127, 0.1)",
  border: "2px solid rgba(127, 127, 127, 0.1)",
  borderRadius: "4px",
  maxWidth: "600px",
  padding: "1rem",
  width: "100%",
};

const titleStyle: CSSProperties = {
  margin: 0,
};

const extraContainerStyle: CSSProperties = {
  width: "100%",
};

const linkContainerStyle: CSSProperties = {
  display: "flex",
  alignItems: "end",
  justifyContent: "space-between",
};

const dataProtectionStyle: CSSProperties = {
  textAlign: "left",
};

const dataPolicyStyle: CSSProperties = {
  textAlign: "right",
};

export interface LoginViewProps {
  logoElement?: ReactNode | null;
  hrefDataProtectionGeneral?: string | undefined;
  hrefDataProtectionEmployees?: string | undefined;
  hrefDataProtectionBusinessPartners?: string | undefined;
  hrefCookiePolicy?: string | undefined;
  hrefImprint?: string | undefined;
}

export const LoginView: FC<LoginViewProps> = (
  {
    logoElement,
    hrefDataProtectionGeneral,
    hrefDataProtectionEmployees,
    hrefDataProtectionBusinessPartners,
    hrefCookiePolicy,
    hrefImprint,
  },
) => {
  useTranslation();

  const navigate = useNavigate();
  const loginNavigationFn = useMemo(
    () => () => navigate("/"),
    [navigate],
  );

  const { isAuthenticated } = useOidc();
  useEffect(
    () => {
      if (isAuthenticated) {
        loginNavigationFn();
      }
    },
    [isAuthenticated, loginNavigationFn],
  );

  return (
    <div style={centerContainerStyle}>
      <Result
        icon={logoElement}
        style={antdResultStyle}
        title={(
          <Typography.Title level={2} style={titleStyle}>
            <Trans ns="common" i18nKey="oidc.loginView.title"/>
          </Typography.Title>
        )}
        extra={(
          <Space direction="vertical" size="middle" style={extraContainerStyle}>
            {!isAuthenticated && (
              <Button type="primary" icon={<LoginOutlined/>} onClick={loginNavigationFn}>
                <Trans ns="common" i18nKey="oidc.loginView.loginButton"/>
              </Button>
            )}
            {isAuthenticated && (
              <Space size="middle">
                <Spin size="default" spinning={true}/>
                <Trans ns="common" i18nKey="oidc.loginView.labelRedirecting"/>
              </Space>
            )}
            <div style={linkContainerStyle}>
              <div style={dataProtectionStyle}>
                <div>
                  <Button type="link" href={hrefDataProtectionGeneral} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionGeneral"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={hrefDataProtectionEmployees} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionEmployees"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={hrefDataProtectionBusinessPartners} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.dataProtectionBusinessPartners"/>
                  </Button>
                </div>
              </div>
              <div style={dataPolicyStyle}>
                <div>
                  <Button type="link" href={hrefCookiePolicy} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.cookiePolicy"/>
                  </Button>
                </div>
                <div>
                  <Button type="link" href={hrefImprint} target="_blank" rel="noreferrer">
                    <Trans ns="common" i18nKey="oidc.loginView.imprint"/>
                  </Button>
                </div>
              </div>
            </div>
          </Space>
        )}
      />
    </div>
  );
};
